import { Material } from '@babylonjs/core/Materials/material'
import '@babylonjs/core/Materials/Node'
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial'
import '@babylonjs/core/Materials/standardMaterial'
import { Texture } from '@babylonjs/core/Materials/Textures/texture'
import { Color3 } from '@babylonjs/core/Maths/math.color'
import { Scene } from '@babylonjs/core/scene'
import { GetUrl, Settings } from './Settings'
import { TrackException } from './Telemetry'

export const requestQueue: Record<string, Promise<Material>> = {}

export default async function getMaterial(scene: Scene, name: string, useFallback: boolean = false): Promise<Material> {
	try {
		let mat = scene.getMaterialByName(name)
		if (!mat) {
			if (!requestQueue[name]) requestQueue[name] = downloadMaterial(scene, name, useFallback)
			mat = await requestQueue[name]
			requestQueue[name] = null
		}
		return mat
	} catch (error) {
		TrackException(error, { type: 'LoadMaterial', name, useFallback })
		console.warn('getMaterial FAILED ', name, error)
		if (useFallback) return get404Material(name, scene)
		else throw error
	}
}

export function getImportMaterial(scene: Scene) {
	if (scene.getMaterialByName('IMPORTED')) return scene.getMaterialByName('IMPORTED')

	const defaultMaterial = new PBRMaterial('IMPORTED', scene)
	defaultMaterial.albedoColor = new Color3(1, 0, 1)
	// defaultMaterial.unlit = true
	defaultMaterial.roughness = 1
	defaultMaterial.metallic = 0
	return defaultMaterial
}

export function get404Material(name: string, scene: Scene) {
	const material = (scene.getMaterialByName(name) as PBRMaterial) || new PBRMaterial(name, scene)
	material.albedoTexture = getDefaultPattern(scene)
	material.albedoColor = Color3.Teal()
	material.metallic = 0
	material.roughness = 1
	material.metadata = { isFallback: true }
	return material
}

export function getDefaultPattern(scene: Scene) {
	const dash = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAQElEQVQ4je2QsQ0AIAzDbP7/OSwcgESWSnjpZjk1CS3U2BQCrKbsF74zo/BKqF7NSGJ18owf/sI3ZhQC5FRW7gaytFPr0mGCsAAAAABJRU5ErkJggg=='
	const texture = (scene.getTextureByName('data:DefaultPattern') as Texture) || Texture.CreateFromBase64String(dash, 'DefaultPattern', scene)
	texture.wAng = Math.PI / 4
	texture.uScale = texture.vScale = 200
	return texture
}

export function GetDefaultMaterial(scene: Scene) {
	const material = (scene.getMaterialByName('DefaultMaterial') as PBRMaterial) || new PBRMaterial('DefaultMaterial', scene)
	material.albedoTexture = getDefaultPattern(scene)
	material.albedoColor = Color3.Magenta()
	material.metallic = 0
	material.roughness = 1
	material.metadata = { isFallback: true }
	return material
}

async function downloadMaterial(scene: Scene, name: string, useFallback): Promise<Material> {
	let response = await fetch(await GetUrl(`/Materials/${name}.json`), { headers: { 'Accept': 'application/json' } })
	if (!response.ok) {
		if (response.status == 404 && useFallback) return get404Material(name, scene)
		console.warn(`Material not loaded: ${name}`)
		throw (new Error(`Material not loaded: ${name}`))
	}
	const json = await response.json()
	const textureKeys = Object.keys(json).filter(x => x.endsWith('Texture')).filter(x => json[x].url)
	for (const key of textureKeys) {
		json[key].url = json[key].name = GetUrl(json[key].url)
	}
	delete json.plugins?.DecalMapConfiguration
	const material = scene.getMaterialByName(name) || Material.Parse(json, scene, Settings.baseUrl)
	// if (material?.subSurface) material.subSurface.applyAlbedoAfterSubSurface = true
	material.getActiveTextures().forEach(x => x.name = new URL(x.name).pathname.substring(1))
	const prevName = material.name
	material.name += '-ORIGINAL'
	const clone = material.clone(prevName)
	material.dispose()
	await new Promise((resolve) => Texture.WhenAllReady(clone.getActiveTextures(), resolve as any))
	return clone
}
